import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Building2, FileText } from 'lucide-react';
import { useRevenueAccounts, useRevenueInvoices, useRevenueSummary } from '../hooks/useRevenue';
import { RevenueSummaryCards } from '../components/RevenueSummaryCards';
import { RevenueFilters } from '../components/RevenueFilters';
import { RevenueAccountsTable } from '../components/RevenueAccountsTable';
import { RevenueInvoicesTable } from '../components/RevenueInvoicesTable';
import type { RevenueQueryParams } from '../types';

type Tab = 'invoices' | 'accounts';

export function RevenuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as Tab) || 'invoices';

  // Filter state
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [year, setYear] = useState<number | undefined>(2025);
  const [month, setMonth] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination state (separate per tab)
  const [invoicesPage, setInvoicesPage] = useState(0);
  const [accountsPage, setAccountsPage] = useState(0);

  // Sort state (separate per tab)
  const [invoicesSortKey, setInvoicesSortKey] = useState('invoice_date');
  const [invoicesSortDir, setInvoicesSortDir] = useState<1 | -1>(-1);
  const [accountsSortKey, setAccountsSortKey] = useState('account_code');
  const [accountsSortDir, setAccountsSortDir] = useState<1 | -1>(1);

  // Tab switching
  const setTab = useCallback((tab: Tab) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  // Reset pagination on filter change
  const resetPages = useCallback(() => {
    setInvoicesPage(0);
    setAccountsPage(0);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    resetPages();
  }, [resetPages]);

  const handleCategoryChange = useCallback((value: string) => {
    setCategory(value);
    resetPages();
  }, [resetPages]);

  const handleYearChange = useCallback((value: number | undefined) => {
    setYear(value);
    resetPages();
  }, [resetPages]);

  const handleMonthChange = useCallback((value: string) => {
    setMonth(value);
    resetPages();
  }, [resetPages]);

  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    resetPages();
  }, [resetPages]);

  const handleDateToChange = useCallback((value: string) => {
    setDateTo(value);
    resetPages();
  }, [resetPages]);

  const handleClearAll = useCallback(() => {
    setSearch('');
    setCategory('');
    setYear(undefined);
    setMonth('');
    setDateFrom('');
    setDateTo('');
    resetPages();
  }, [resetPages]);

  // Sort handlers
  const handleInvoicesSort = useCallback((key: string) => {
    setInvoicesSortKey((prevKey) => {
      if (prevKey === key) {
        setInvoicesSortDir((prev) => (prev === 1 ? -1 : 1));
        return key;
      }
      setInvoicesSortDir(1);
      return key;
    });
    setInvoicesPage(0);
  }, []);

  const handleAccountsSort = useCallback((key: string) => {
    setAccountsSortKey((prevKey) => {
      if (prevKey === key) {
        setAccountsSortDir((prev) => (prev === 1 ? -1 : 1));
        return key;
      }
      setAccountsSortDir(1);
      return key;
    });
    setAccountsPage(0);
  }, []);

  // Build query params
  const baseParams: RevenueQueryParams = {
    search: search || undefined,
    category: category || undefined,
    year,
    month: month || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const invoicesParams: RevenueQueryParams = {
    ...baseParams,
    page_number: invoicesPage,
    page_length: 25,
    sort_key: invoicesSortKey,
    sort_direction: invoicesSortDir,
  };

  const accountsParams: RevenueQueryParams = {
    ...baseParams,
    page_number: accountsPage,
    page_length: 25,
    sort_key: accountsSortKey,
    sort_direction: accountsSortDir,
  };

  // Queries
  const { data: summary, isLoading: summaryLoading } = useRevenueSummary({ year, month: month || undefined });
  const { data: invoicesData, isLoading: invoicesLoading } = useRevenueInvoices(invoicesParams);
  const { data: accountsData, isLoading: accountsLoading } = useRevenueAccounts(accountsParams);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Revenue</h1>
        <p className="text-sm text-muted-foreground">Track revenue by account, invoice, and category</p>
      </div>

      {/* Summary Cards */}
      <RevenueSummaryCards data={summary} loading={summaryLoading} />

      {/* Filters */}
      <RevenueFilters
        search={search}
        category={category}
        year={year}
        month={month}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={handleSearchChange}
        onCategoryChange={handleCategoryChange}
        onYearChange={handleYearChange}
        onMonthChange={handleMonthChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        onClearAll={handleClearAll}
      />

      {/* Tab Switch */}
      <div className="flex items-center gap-2">
        <Button
          variant={activeTab === 'invoices' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('invoices')}
        >
          <FileText className="mr-1.5 h-4 w-4" />
          Invoices
        </Button>
        <Button
          variant={activeTab === 'accounts' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('accounts')}
        >
          <Building2 className="mr-1.5 h-4 w-4" />
          Accounts
        </Button>
      </div>

      {/* Table */}
      {activeTab === 'invoices' ? (
        <RevenueInvoicesTable
          data={invoicesData}
          loading={invoicesLoading}
          sortKey={invoicesSortKey}
          sortDirection={invoicesSortDir}
          onSort={handleInvoicesSort}
          onPageChange={setInvoicesPage}
        />
      ) : (
        <RevenueAccountsTable
          data={accountsData}
          loading={accountsLoading}
          sortKey={accountsSortKey}
          sortDirection={accountsSortDir}
          onSort={handleAccountsSort}
          onPageChange={setAccountsPage}
        />
      )}
    </div>
  );
}
